import {
  UPSERT_PROJECT_METADATA, UPSERT_RELEASE_METADATA, REMOVE_PROJECT_CONTEXT, REMOVE_RELEASE_CONTEXT,
  SET_COMPANY,
  SET_PROJECT,
  SET_RELEASE,
  SET_RELEASE_NULL,
  SET_COMPONENT_FILTER,
  SET_SEVERITY_FILTER,
  SET_COMPANY_LIST,
  SET_PROJECT_LIST,
  SET_RELEASE_LIST,
  SET_PROJECT_LIST_LOADING,
  SET_RELEASE_LIST_LOADING,
  SHOW_DEFECT_FILTERS,
  HIDE_DEFECT_FILTERS,
  SHOW_RELEASE_SELECTOR,
  HIDE_RELEASE_SELECTOR,
  RESET_DEFECT_FILTERS,
  SET_USER_AUTHENTICATED,
  SET_CRITICAL_ERROR,
  SET_DEMO_STATE,
  SET_COMPANY_EXPIRED,
  SET_NO_COMPANIES,
  SET_COMPANY_ROLES,
  SET_USER_TIMEZONE,
} from "./actionTypes";
import { computeNoCompanies } from './companySelection';

const initialState = {
  company: undefined,
  project: undefined,
  release: undefined,
  componentFilter: [],
  severityFilter: [],
  companyList: [],
  projectList: [],
  releaseList: [],
  // True while the project/release list request is in flight (read by the
  // modern context selectors only).
  projectListLoading: false,
  releaseListLoading: false,
  componentList: [],
  severityList: [],
  showDefectFilter: false,
  showReleaseSelector: false,
  releaseNull: false,
  isAuthenticated: false,
  isAdmin: false,
  jwtToken: undefined,
  criticalError: undefined,
  demoEnabled: undefined,
  demoMode: false,  // New flag for OIDC-based demo mode detection
  baseApiUri: '',
  company_expired: false,
  no_companies: false,
  companyRoles: {},
  permissionsError: false,
};

export default function Reducer(state=initialState, action) {
  switch (action.type) {

    case SET_COMPANY: {
      const { company } = action;

      // A non-admin whose only company is the demo still needs the onboarding
      // flow, so no_companies is derived from the full list + admin status
      // rather than from whether a (possibly demo) company got selected.
      const no_companies = computeNoCompanies(state.companyList, state.isAdmin);

      return {
        ...state,
        company,
        no_companies,
        project: undefined,
        release: undefined,
        componentFilter: [],
        severityFilter: [],
        projectList: [],
        releaseList: [],
        projectListLoading: false,
        releaseListLoading: false,
        componentList: [],
        severityList: [],
      }
    }

    case UPSERT_PROJECT_METADATA: {
      const { project } = action;
      if (project.company_id !== state.company?.id) return state;
      const found = state.projectList.some(item => item.id === project.id);
      const projectList = found ? state.projectList.map(item => item.id === project.id ? { ...item, ...project } : item) : [...state.projectList, project];
      return { ...state, projectList,
        project: state.project?.id === project.id ? projectList.find(item => item.id === project.id) : state.project };
    }
    case UPSERT_RELEASE_METADATA: {
      const { release } = action;
      if (release.project_id !== state.project?.id) return state;
      const found = state.releaseList.some(item => item.id === release.id);
      const releaseList = found ? state.releaseList.map(item => item.id === release.id ? { ...item, ...release } : item) : [...state.releaseList, release];
      return { ...state, releaseList,
        release: state.release?.id === release.id ? releaseList.find(item => item.id === release.id) : state.release };
    }
    case REMOVE_PROJECT_CONTEXT: {
      const projectList = state.projectList.filter(item => item.id !== action.project.id);
      if (state.project?.id !== action.project.id) return { ...state, projectList };
      return { ...state, projectList, project: undefined, release: undefined, releaseNull: true,
        releaseList: [], componentList: [], severityList: [], componentFilter: [], severityFilter: [] };
    }
    case REMOVE_RELEASE_CONTEXT: {
      if (state.project?.id !== action.release.project_id) return state;
      const releaseList = state.releaseList.filter(item => item.id !== action.release.id);
      if (state.release?.id !== action.release.id) return { ...state, releaseList };
      return { ...state, releaseList, release: undefined, releaseNull: true,
        componentList: [], severityList: [], componentFilter: [], severityFilter: [] };
    }

    case SET_PROJECT: {
      const { project } = action;
      return { ...state, project, releaseNull: false, release: undefined,
        releaseList: [], releaseListLoading: false, componentList: [], severityList: [], componentFilter: [], severityFilter: [] }
    }

    case SET_RELEASE: {
      const { release, response } = action;

      const defectFilterConfig = response.defect_filter_config;
      const componentList = defectFilterConfig ? defectFilterConfig.components.sort((a, b) => (a.name > b.name) ? 1 : -1) : [];
      const severityList = defectFilterConfig ? defectFilterConfig.severities.sort((a, b) => (a.name > b.name) ? 1 : -1) : [];

      return { ...state, release, componentList, severityList }
    }

    case SET_RELEASE_NULL: {
      return { ...state, releaseNull: true, releaseListLoading: false }
    }

    case SET_COMPONENT_FILTER: {
      const { componentFilter } = action;
      return { ...state, componentFilter }
    }

    case SET_SEVERITY_FILTER: {
      const { severityFilter } = action;
      return { ...state, severityFilter }
    }

    case RESET_DEFECT_FILTERS: {
      return { ...state, componentFilter: [], severityFilter: [] }
    }

    case SHOW_DEFECT_FILTERS: {
      return { ...state, showDefectFilter: true }
    }

    case HIDE_DEFECT_FILTERS: {
      return { ...state, showDefectFilter: false }
    }

    case SHOW_RELEASE_SELECTOR: {
      return { ...state, showReleaseSelector: true }
    }

    case HIDE_RELEASE_SELECTOR: {
      return { ...state, showReleaseSelector: false }
    }

    case SET_COMPANY_LIST: {
      const { companies } = action;
      return { ...state, companyList: companies }
    }

    case SET_PROJECT_LIST: {
      const { projects } = action;
      return { ...state, projectList: projects, projectListLoading: false }
    }

    case SET_PROJECT_LIST_LOADING: {
      return { ...state, projectListLoading: Boolean(action.loading) }
    }

    case SET_RELEASE_LIST_LOADING: {
      return { ...state, releaseListLoading: Boolean(action.loading) }
    }

    case SET_RELEASE_LIST: {
      const { releases } = action;
      return { ...state, releaseList: releases, releaseListLoading: false }
    }

    case SET_USER_AUTHENTICATED: {
      const { isAuthenticated, isAdmin, jwtToken, demoMode = false, userContext, isNewSignup = false, companyRoles = {}, permissionsError = false } = action;
      return { ...state, isAuthenticated, isAdmin, jwtToken, demoMode, userContext, isNewSignup, companyRoles, permissionsError }
    }

    case SET_USER_TIMEZONE: {
      return { ...state, userContext: { ...(state.userContext || {}), timezone: action.timezone } }
    }

    case SET_COMPANY_ROLES: {
      const { companyRoles = {}, isAdmin = false } = action;
      return { ...state, companyRoles, isAdmin, permissionsError: false }
    }

    case SET_DEMO_STATE: {
      const { enabled, baseApiUri } = action;
      return { ...state, demoEnabled: enabled, baseApiUri }; 
    }

    case SET_CRITICAL_ERROR: {
      const { error } = action;
      return { ...state, criticalError: error } 
    }

    case SET_COMPANY_EXPIRED: {
      const { isExpired } = action;

      // no_companies is derived and set by SET_COMPANY; don't clobber it here
      // (a demo-only user's no_companies stays true even though a — demo —
      // company is selected and not expired).
      return { ...state, company_expired: isExpired }
    }

    case SET_NO_COMPANIES: {
      return { ...state, no_companies: true, company_expired: false } 
    }

    default: {
      return state;
    }
  }
}
